
import React from "react";
import { Capability, Schemes, Utils } from "../../../../utils";
import { i18nManager } from "../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { Elements } from "../../../shared/Elements";
import MediaInput from "../../../shared/MediaInput";
import StatefulLink from "../../../shared/StatefulLink";
import { ConfirmDialog, Siren } from "../../../shared/ConfirmDialog";
import LoadingGif from "../../../../assets/images/loading_circle.gif";
import { Button, ButtonManageRef, Checkbox, DateHelper, DateTimeInput, Dropdown, Form, FormControl, Scheme, TextAreaInput, TextInput, useScreenDimension } from "@rivtn/noseur";
import { ApiResponseData, BaseService, ConsusService, HostService, IHost, IHostEvent, IHostWebsite, IMember, IProfileWebsite, ITeam, IUploadedFile, MemberService, SanitizedResponse } from "../../../../services";
import UpSellPlan from "../../../shared/UpSellPlan";

export interface EventsProps {
    host: IHost;
    profile: IMember;
    hostWebsite: IHostWebsite;
}

function Events(props: EventsProps) {

    const { host } = props;
    const now = new Date().getTime();
    const { width } = useScreenDimension();
    const hostService = HostService.getInstance();
    const consusService = ConsusService.getInstance();
    const labels = i18nManager.Labels.host.settings.events;
    const [profile, _] = React.useState<IMember>(props.profile);
    const [events, setEvents] = React.useState<ApiResponseData<IHostEvent>>({});
    const [hostWebsite, setHostWebsite] = React.useState<IHostWebsite>(props.hostWebsite);
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const maxSponsors = 5; // TODO FETCH FROM PLAN

    React.useEffect(fetchEvents, []);

    return (<div className="profile-settings team-settings">
        <Checkbox scheme={Schemes.OMNEHOST} label={labels.enable_ads} defaultChecked={hostWebsite.events_settings.enable_rivtn_ad} onChecked={(checked) => {
            hostService.updateHostWebsite(host.external_id, { ...hostWebsite, events_settings: { ...hostWebsite.events_settings, enable_rivtn_ad: checked } }).then(({ }: SanitizedResponse<IHostWebsite>) => {
                Siren.alert(labels.update_successful);
            }).catch(BaseService.reportError);
        }} />
        <hr style={{ marginBottom: 0 }} />
        <div className="pagination">
            {Elements.buildPaginator(events ?? {},
                ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, size }, setSearchParams, fetchEvents),
                parseInt(searchParams.get("page") ?? "1"), { margin: 15, marginBottom: 5 })}
            {(width > 800) && Elements.buildSorter(labels.sorts, (sort) => Utils.updateSearchParamses({ sort }, setSearchParams, fetchEvents), labels.sorts[0].label)}
        </div>
        <div style={{ display: "flex", marginTop: 10, justifyContent: "space-between", flexDirection: (width > 800 ? "row" : "column") }}>
            <Form scheme={Schemes.OMNEHOST} style={{ display: "flex", flexDirection: (width > 800 ? "row" : "column") }} >
                <Dropdown selectedOptionIndex={parseInt(searchParams.get("status") ?? "0")} formControlProps={{
                    isFieldset: true,
                    label: labels.status,
                    style: { width: (width > 800 ? 150 : "100%") }
                }} options={labels.statuses} onSelectOption={(o) => {

                }} />
                {(width > 800) && (<>
                    <FormControl isFieldset label={labels.title} style={{ marginLeft: 15, width: 200 }}>
                        <TextInput defaultValue={searchParams.get("title") ?? ""} onInputComplete={(v) => {
                            Utils.updateSearchParams("title", v, setSearchParams, fetchEvents);
                        }} />
                    </FormControl>
                    <FormControl isFieldset label={labels.details} style={{ marginLeft: 15, width: 200 }}>
                        <TextInput defaultValue={searchParams.get("details") ?? ""} onInputComplete={(v) => {
                            Utils.updateSearchParams("details", v, setSearchParams, fetchEvents);
                        }} />
                    </FormControl>
                </>)}
            </Form>
            <UpSellPlan capability={Capability.MAX_HOST_WEBSITE_EVENTS_UPLOAD} currentValue={events.total_elements} childrenRelayProps={Elements.BUTTON_UPGRADE_PROPS} >
                <Button raised scheme={Schemes.OMNEHOST} text={labels.add_event} style={{ marginTop: (width > 800 ? 10 : 15), padding: "10px 35px" }} onClick={() => addOrUpdateEvent()} />
            </UpSellPlan>
        </div>
        <hr style={{ margin: "20px 0px" }} />
        {!events.content ? Elements.LOADING_STATE : (<div className="ho-events">
            {events.content?.map((event) => {
                const unknown = !event.end_date && !event.start_date;
                const ended = event.end_date && DateHelper.isPast(new Date(event.end_date));
                const upcoming = event.start_date && DateHelper.isFuture(new Date(event.start_date));
                const ongoing = !ended && event.start_date && DateHelper.isPast(new Date(event.start_date));

                return (<div key={event.external_id} className="ho-event" onClick={() => addOrUpdateEvent(event)}>
                    <img alt={event.title} src={event.media_location} />
                    <div className="details">
                        <div className="r1 lined-limit">
                            <span className="t">{event.title}</span>&nbsp;
                            {(event.start_date || event.end_date) && " - "}
                            {event.start_date && (Utils.formatToFineDate(event.start_date))}
                            {(event.start_date && event.end_date) && " - "}
                            {event.end_date && (Utils.formatToFineDate(event.end_date))}
                        </div>
                        <div className="dets lined-limit">{event.details}</div>
                        <div className="rl">
                            <span className="stat">
                                {ended && labels.statuses_labels.ended}
                                {unknown && labels.statuses_labels.unknown}
                                {ongoing && labels.statuses_labels.ongoing}
                                {upcoming && labels.statuses_labels.upcoming}
                            </span>
                            {(event.action_label || event.action_location || event.location) && (" - ")}
                            <span className="loc">{event.location}</span>
                            {(event.action_label || event.action_location) && (<>
                                &nbsp;{" - "}
                                ({event.action_label} - {event.action_location})
                            </>)}
                        </div>
                    </div>
                </div>);
            })}
        </div>)}
        {events.content && !events.content.length && (Elements.buildEmptyListView("fa fa-circle", (<span>
            {labels.empty_label_parts.no_event_found}{" "}
            <span className="link" onClick={() => addOrUpdateEvent()}>{labels.empty_label_parts.create}</span>{" "}
            {labels.empty_label_parts.an_event}
        </span>)))}
    </div>);

    function fetchEvents() {
        hostService.queryHostEvents(host.external_id, {
            title: searchParams.get("title"),
            status: searchParams.get("status"),
            details: searchParams.get("details"),
            sort: searchParams.get("sort") ?? "",
            page: searchParams.get("page") ?? "1",
            size: searchParams.get("size") ?? "100",
        }).then(({ apiResponse }: SanitizedResponse<IHostEvent>) => {
            setEvents(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function addOrUpdateEvent(event: IHostEvent = {} as any) {
        const dialogLabels = labels.dialog.add_update;
        const dialogErrorRef = { current: null } as any;
        const mediaInputManageRef = { current: undefined as any };

        const dialog = ConfirmDialog({
            closable: true,
            style: { minWidth: (width < 800 ? "95%" : 560), maxWidth: "90%" },
            confirmLabel: (event.external_id ? dialogLabels.update : dialogLabels.add),
            title: (event.external_id ? dialogLabels.update_event : dialogLabels.add_event),
            footerElements: (event.external_id
                ? (<Button raised scheme={Scheme.DANGER} text={dialogLabels.delete} style={{ padding: "10px 35px" }} onClick={() => {
                    dialog.hide(() => deleteEvent(event));
                }} />)
                : undefined),
            content: (<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginTop: 20 }}>
                <MediaInput instruction={i18nManager.Labels.common.click_to_choose} style={{ alignSelf: "center" }}
                    default={event.media_location ? event.media_location + "?n=" + now : undefined}
                    manageRef={mediaInputManageRef} width={"100%"} height={300} maxFileSize={20000000}
                    onValidationFail={(_: File, error: string) => {
                        dialogErrorRef.current!.innerText = error;
                        return true;
                    }} />
                <FormControl fill isFieldset label={dialogLabels.title} {...Elements.FCFL} style={{ flex: 1 }}>
                    <TextInput defaultValue={event.title} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        event.title = v;
                    }} />
                </FormControl>
                <Form scheme={Schemes.OMNEHOST} style={{ display: "flex", width: "100%", marginTop: 10 }}>
                    <FormControl isFieldset label={dialogLabels.start_date} {...Elements.FCFL} style={{ flex: 1 }}>
                        <DateTimeInput fill selectedDates={event.start_date ? [new Date(event.start_date)] : undefined} onSelectDate={(v) => {
                            dialogErrorRef.current!.innerText = " ";
                            event.start_date = v.selectedDate!.toISOString();
                        }} />
                    </FormControl>
                    <FormControl isFieldset label={dialogLabels.end_date} {...Elements.FCFL} style={{ marginLeft: 20, flex: 1 }}>
                        <DateTimeInput fill selectedDates={event.end_date ? [new Date(event.end_date)] : undefined} onSelectDate={(v) => {
                            dialogErrorRef.current!.innerText = " ";
                            event.end_date = v.selectedDate!.toISOString();
                        }} />
                    </FormControl>
                </Form>
                <FormControl fill isFieldset label={dialogLabels.location} {...Elements.FCFL} style={{ marginTop: 10 }}>
                    <TextInput defaultValue={event.location} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        event.location = v;
                    }} />
                </FormControl>
                <Form scheme={Schemes.OMNEHOST} style={{ display: "flex", width: "100%", marginTop: 10 }}>
                    <FormControl isFieldset label={dialogLabels.action_label} {...Elements.FCFL} style={{ flex: 1 }}>
                        <TextInput defaultValue={event.action_label} onInputComplete={(v) => {
                            dialogErrorRef.current!.innerText = " ";
                            event.action_label = v;
                        }} />
                    </FormControl>
                    <FormControl isFieldset label={dialogLabels.action_loc} {...Elements.FCFL} style={{ marginLeft: 20, flex: 1 }}>
                        <TextInput defaultValue={event.action_location} onInputComplete={(v) => {
                            dialogErrorRef.current!.innerText = " ";
                            event.action_location = v;
                        }} />
                    </FormControl>
                </Form>
                <FormControl fill isFieldset label={dialogLabels.details} {...Elements.FCFL} style={{ marginTop: 10 }}>
                    <TextAreaInput fill style={{ height: 180 }} defaultValue={event.details} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        event.details = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>
            </div>),
            onConfirm: (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                const files = mediaInputManageRef.current?.files() as File[];
                button?.setLoadingState(true);
                const triggerActualAction = (mediaLocation?: string) => {
                    if (mediaLocation) {
                        event.media_location = mediaLocation;
                    }
                    const method = (event.external_id ? hostService.updateHostEvent : hostService.createHostEvent).bind(hostService);
                    method(host.external_id, event).then(() => {
                        Siren.alert(event.external_id ? dialogLabels.update_successful : dialogLabels.add_successful);
                        dialog.hide();
                        fetchEvents();
                    }).catch((error: any) => {
                        dialogErrorRef.current!.innerText = error.errorMessage;
                        BaseService.reportError(error);
                        button?.setLoadingState(false);
                    });
                }
                if (files.length) {
                    const file = files[0];
                    const fileName = (event.external_id && event.media_location ? event.media_location.substring(event.media_location.lastIndexOf("/") + 1) : `${profile.janus_user_external_id}_${file.name.substring(file.name.length - 10, file.name.length)}`);
                    consusService.uploadFile("/omnehost/host/events/", fileName, file).then(async ({ sanitized }: SanitizedResponse<IUploadedFile>) => {
                        triggerActualAction(sanitized.consus_download_url);
                    }).catch((error: any) => {
                        dialogErrorRef.current!.innerText = error.errorMessage;
                        BaseService.reportError(error);
                        button?.setLoadingState(false);
                    });
                } else {
                    if (!event.media_location) {
                        dialogErrorRef.current!.innerText = dialogLabels.choose_feature_img;
                        button?.setLoadingState(false);
                        return false;
                    }
                    triggerActualAction();
                }
                return false;
            }
        });
        dialog.show();
    }

    function deleteEvent(event: IHostEvent) {
        const dialogLabels = labels.dialog.delete;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            style: { maxWidth: 500 },
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            desc: dialogLabels.desc.replace("${title}", event.title),
            content: (<div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>),
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                dialogErrorRef.current!.innerText = " ";
                const removeFromStore = () => {
                    hostService.deleteHostEvent(host.external_id, event).then(() => {
                        Siren.alert(dialogLabels.successful);
                        dialog.hide();
                        fetchEvents();
                    }).catch((error: any) => {
                        dialogErrorRef.current!.innerText = error.errorMessage;
                        BaseService.reportError(error);
                        button?.setLoadingState(false);
                    });
                };
                consusService.deleteFile([event.media_location]).then(removeFromStore).catch(removeFromStore);
                return false;
            }
        });
        dialog.show();
    }

}

export default Events;
